/**
 * Service adapters.
 *
 *   Layer Operation → Adapter (per service type) → Service call → Map update
 *
 * Only operations whose IMPLEMENTATION differs per service live here. Pure
 * state operations (rename, reorder, opacity, visibility) are service-agnostic
 * and handled by the caller, which is why there is no adapter method for them.
 */

import type { AppModule, Layer } from '../types';
import { detectLayerSource } from './capabilities';
import { arcgisServiceUrlFor, getArcgisBaseUrl, getArcgisExtentBounds, withArcgisToken } from '../utils/arcgis';

export interface LayerDetails {
  /** Rows shown in the Layer Information panel. */
  rows: { label: string; value: string }[];
}

export interface LayerAdapter {
  /** Fetches the layer's current extent as [west, south, east, north]. */
  fetchExtent(layer: Layer, module: AppModule): Promise<[number, number, number, number] | null>;
  /** Fetches service metadata for the Layer Information panel. */
  fetchDetails(layer: Layer, module: AppModule): Promise<LayerDetails>;
}

/* ─────────────────────────── ArcGIS (Feature/Map) ────────────────────────── */

const arcgisAdapter: LayerAdapter = {
  async fetchExtent(layer, module) {
    // The layer's OWN service, when it differs from the module's primary
    // `arcgisUrl` — see `arcgisServiceUrlFor`.
    const effectiveUrl = arcgisServiceUrlFor(layer, module);
    const baseUrl = getArcgisBaseUrl(effectiveUrl);
    if (!baseUrl || layer.arcgisLayerId === undefined) return null;

    // The layer's real data extent, already projected to WGS84 by the server.
    // Preferred over the declared extent, which is frequently wrong.
    const params = new URLSearchParams({
      where: '1=1',
      returnExtentOnly: 'true',
      outSR: '4326',
      f: 'json',
    });

    try {
      const url = withArcgisToken(`${baseUrl}/${layer.arcgisLayerId}/query?${params.toString()}`, effectiveUrl);
      const response = await fetch(url);
      if (!response.ok) {
        console.error(`[layers] ArcGIS extent failed (${response.status}) layer=${layer.name}`);
        return null;
      }
      const data = await response.json();
      if (data?.error) {
        console.error(`[layers] ArcGIS extent error layer=${layer.name}: ${data.error.message}`);
        return null;
      }
      return getArcgisExtentBounds(data?.extent) ?? null;
    } catch (error) {
      console.error(`[layers] ArcGIS extent request failed layer=${layer.name}`, error);
      return null;
    }
  },

  async fetchDetails(layer, module) {
    const effectiveUrl = arcgisServiceUrlFor(layer, module);
    const baseUrl = getArcgisBaseUrl(effectiveUrl);
    const rows: { label: string; value: string }[] = [];
    if (!baseUrl || layer.arcgisLayerId === undefined) return { rows };

    // The layer's own REST endpoint — shown even if the metadata fetch below
    // fails, so a service that is unreachable still tells someone where it
    // was supposed to be reachable AT.
    const layerUrl = `${baseUrl}/${layer.arcgisLayerId}`;
    rows.push({ label: 'REST URL', value: layerUrl });

    try {
      const response = await fetch(withArcgisToken(`${layerUrl}?f=json`, effectiveUrl));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data?.error) throw new Error(data.error.message);

      const add = (label: string, value: unknown) => {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          rows.push({ label, value: String(value) });
        }
      };

      add('Service layer name', data.name);
      add('Layer ID', layer.arcgisLayerId);
      add('Geometry type', String(data.geometryType ?? '').replace('esriGeometry', ''));
      add('Description', data.description);
      add('Capabilities', data.capabilities);
      add('Max record count', data.maxRecordCount);
      add('Fields', Array.isArray(data.fields) ? `${data.fields.length} fields` : undefined);
      add('Has attachments', data.hasAttachments ? 'Yes' : 'No');
      add('Min scale', data.minScale || undefined);
      add('Max scale', data.maxScale || undefined);
    } catch (error) {
      console.error(`[layers] ArcGIS details failed layer=${layer.name}`, error);
      rows.push({ label: 'Error', value: 'Service details could not be loaded.' });
    }

    return { rows };
  },
};

/* ────────────────────────────── GeoServer WMS ────────────────────────────── */

/**
 * The GeoServer endpoint, in one protocol's own path convention.
 *
 * `module.geoServerUrl` is saved however it was typed — sometimes ending in
 * `/wms`, sometimes `/wfs`, sometimes neither — so this normalises to
 * whichever protocol is being asked for rather than assuming the saved
 * suffix already matches.
 */
function geoServerServiceUrl(geoServerUrl: string, protocol: 'wms' | 'wfs'): string | null {
  try {
    const url = new URL(geoServerUrl.trim());
    const path = url.pathname.replace(/\/+$/, '');
    url.pathname = /\/(wms|wfs)$/i.test(path) ? path.replace(/\/(wms|wfs)$/i, `/${protocol}`) : `${path}/${protocol}`;
    url.search = '';
    return url.toString();
  } catch {
    return null;
  }
}

/** This one layer's WMS URL — what the map itself requests to draw it. */
function wmsLayerUrl(geoServerUrl: string, layerName: string): string | null {
  const base = geoServerServiceUrl(geoServerUrl, 'wms');
  if (!base) return null;
  const url = new URL(base);
  url.searchParams.set('service', 'WMS');
  url.searchParams.set('request', 'GetMap');
  url.searchParams.set('version', '1.3.0');
  url.searchParams.set('layers', layerName);
  return url.toString();
}

/** This one layer's WFS URL — what Query/Filter/Select request its features
 *  from, the same shape `getWfsUrl` in App.tsx builds for an actual read. */
function wfsLayerUrl(geoServerUrl: string, layerName: string): string | null {
  const base = geoServerServiceUrl(geoServerUrl, 'wfs');
  if (!base) return null;
  const url = new URL(base);
  url.searchParams.set('service', 'WFS');
  url.searchParams.set('request', 'GetFeature');
  url.searchParams.set('version', '2.0.0');
  url.searchParams.set('typeNames', layerName);
  url.searchParams.set('outputFormat', 'application/json');
  return url.toString();
}

const wmsAdapter: LayerAdapter = {
  async fetchExtent(layer, module) {
    if (!module.geoServerUrl) return null;

    // WMS publishes extents only through GetCapabilities.
    try {
      const url = new URL(module.geoServerUrl.trim());
      url.searchParams.set('service', 'WMS');
      url.searchParams.set('request', 'GetCapabilities');
      url.searchParams.set('version', '1.3.0');

      const response = await fetch(url.toString());
      if (!response.ok) {
        console.error(`[layers] WMS GetCapabilities failed (${response.status})`);
        return null;
      }

      const xml = new DOMParser().parseFromString(await response.text(), 'application/xml');
      const match = Array.from(xml.getElementsByTagName('Layer')).find((node) => {
        const name = Array.from(node.children).find(
          (child) => child.tagName.toLowerCase() === 'name',
        )?.textContent;
        return name?.trim() === layer.name;
      });
      if (!match) return null;

      const box = Array.from(match.children).find(
        (child) => child.tagName.toLowerCase() === 'ex_geographicboundingbox',
      );
      const read = (tag: string) => Number(box?.getElementsByTagName(tag)[0]?.textContent);
      const bounds: [number, number, number, number] = [
        read('westBoundLongitude'),
        read('southBoundLatitude'),
        read('eastBoundLongitude'),
        read('northBoundLatitude'),
      ];
      return bounds.every(Number.isFinite) ? bounds : null;
    } catch (error) {
      console.error(`[layers] WMS extent request failed layer=${layer.name}`, error);
      return null;
    }
  },

  async fetchDetails(layer, module) {
    const rows: { label: string; value: string }[] = [
      { label: 'Service layer name', value: layer.name },
    ];
    if (module.geoServerUrl) {
      rows.push({ label: 'Service URL', value: module.geoServerUrl });
      // Both protocols this one layer answers to: WMS renders it, WFS is
      // what Query/Filter/Select actually read its features through.
      const wms = wmsLayerUrl(module.geoServerUrl, layer.name);
      if (wms) rows.push({ label: 'WMS layer URL', value: wms });
      const wfs = wfsLayerUrl(module.geoServerUrl, layer.name);
      if (wfs) rows.push({ label: 'WFS layer URL', value: wfs });
    }
    if (layer.bounds) {
      rows.push({
        label: 'Extent (WGS84)',
        value: layer.bounds.map((value) => value.toFixed(4)).join(', '),
      });
    }
    rows.push({ label: 'Protocol', value: 'WMS (tiled image), WFS (features)' });
    return { rows };
  },
};

/* ────────────────────────────── Local raster ─────────────────────────────── */

/**
 * A GeoTIFF has no service to ask, but unlike a local vector layer (which
 * falls through to `arcgisAdapter` below and reports nothing — its details
 * were never worth a dedicated adapter) this one already carries metadata
 * worth showing: it was read out of the file itself at import time.
 */
const localRasterAdapter: LayerAdapter = {
  async fetchExtent(layer) {
    // Always already known: a GeoTIFF's georeferencing IS its extent, so
    // there is nothing to fetch that geotiff.ts didn't already compute.
    return layer.bounds ?? null;
  },

  async fetchDetails(layer) {
    const rows: { label: string; value: string }[] = [];
    const raster = layer.localRaster;
    if (raster) {
      if (raster.fileName) rows.push({ label: 'File', value: raster.fileName });
      rows.push({
        label: 'Dimensions',
        value: `${raster.width.toLocaleString()} × ${raster.height.toLocaleString()} px`,
      });
      rows.push({ label: 'Bands', value: String(raster.bandCount) });
      if (raster.projectionEpsg) {
        rows.push({ label: 'Source projection', value: `EPSG:${raster.projectionEpsg}` });
      }
      // `mimeType` is absent only on a layer imported before compression
      // existed — see geotiff.ts's file header for what each shape means.
      rows.push({
        label: 'Storage',
        value: raster.mimeType
          ? `Compressed image (${raster.mimeType})`
          : 'Original GeoTIFF (uncompressed — imported before compression was added)',
      });
    }
    if (layer.bounds) {
      rows.push({
        label: 'Extent (WGS84)',
        value: layer.bounds.map((value) => value.toFixed(4)).join(', '),
      });
    }
    rows.push({ label: 'Protocol', value: 'Local GeoTIFF (raster)' });
    return { rows };
  },
};

/** Picks the adapter for a layer. One entry point, so callers stay generic. */
export function getLayerAdapter(layer: Layer, module: AppModule): LayerAdapter {
  const sourceKind = detectLayerSource(layer, module);
  if (sourceKind === 'wms') return wmsAdapter;
  if (sourceKind === 'local-raster') return localRasterAdapter;
  return arcgisAdapter;
}
